import { BarController, CategoryScale, Chart as ChartJS, Legend, LineController, LineElement, LinearScale, PointElement, Title, Tooltip, scales } from "chart.js";
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

const blinkingBarPlugin = {
  id: 'blinkingBar',
  beforeDatasetsDraw(chart, args, pluginOptions) {
    if (!pluginOptions.shouldBlink) return;

    const dataset = chart.data.datasets[0];
    if (dataset.data[0] === 0) return;

    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const now = Date.now();
    const opacity = Math.abs(Math.sin(now / 500)); // 깜빡임 효과

    ctx.save();
    ctx.globalAlpha = opacity;

    meta.data.forEach((bar) => {
      const { x, y, base, height, width } = bar.getProps(['x', 'y', 'base', 'width', 'height'], true);

      const drawX = Math.min(x, base);
      const drawY = y - height / 2;
      const drawWidth = Math.abs(x - base);
      const drawHeight = height;
      const radius = 6;

      ctx.fillStyle = pluginOptions.barColor || 'red';

      ctx.beginPath();
      ctx.moveTo(drawX, drawY); // 왼쪽 위
      ctx.lineTo(drawX, drawY + drawHeight); // 왼쪽 아래
      
      // 오른쪽 아래 - 둥글게
      ctx.lineTo(drawX + drawWidth - radius, drawY + drawHeight);
      ctx.quadraticCurveTo(drawX + drawWidth, drawY + drawHeight, drawX + drawWidth, drawY + drawHeight - radius);
      
      // 오른쪽 위 - 둥글게
      ctx.lineTo(drawX + drawWidth, drawY + radius);
      ctx.quadraticCurveTo(drawX + drawWidth, drawY, drawX + drawWidth - radius, drawY);
      
      // 다시 왼쪽 위로
      ctx.lineTo(drawX, drawY);
      
      ctx.closePath();
      ctx.fill();
    });

    ctx.restore();
  }
};

ChartJS.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, BarController, Title, Tooltip, Legend, blinkingBarPlugin);

const ChartBox = styled.div`
    width: 100%;
    height: 250px;
    padding: 0rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export default function CrdStatChart({ crdData, eqmtCount }) {

  // 색깔 계산은 최대 크레딧이 아닌 eqmtCount * 10000 + 10000으로 계산
  const maxValue = (eqmtCount * 10000) + 10000; // 그래프 max
  const percentage = (crdData[0] / (eqmtCount * 10000)) * 100;
  const chartRef = useRef(null);

  useEffect(() => {
    let frameId;
    let lastTime = 0;
    let isUnmounted = false;

    const renderLoop = (time) => {
      if (isUnmounted) return;
      if (time - lastTime > 16) { // 100ms = 약 10fps
        lastTime = time;
        chartRef.current?.draw();
      }
      frameId = requestAnimationFrame(renderLoop);
    };
  
    // percentage가 10 미만일 때만 애니메이션 루프 돌림
    if (percentage < 10) {
      frameId = requestAnimationFrame(renderLoop);
    }
  
    // 루프 멈춤
    return () =>{
      isUnmounted = true;
      cancelAnimationFrame(frameId);
    }
  }, [percentage]);

  let stepSize = 0;

  if (maxValue > 50000) {
    stepSize = 10000;
  } else {
    stepSize = 5000;
  }

  let barColor = '';

  if (percentage <= 10) {
    barColor = 'red'
  } else if (percentage > 10 && percentage <= 40) {
    barColor = '#feb907';
  } else if (percentage > 40 && percentage <= 70) {
    barColor = '#ffeb00';
  } else {
    barColor = '#00d45e'
  }

  // const isLow = crdData[0] < (eqmtCount * 10000) * 0.1; // 최대 크레딧의 10% 계산
  // const barColor = isLow ? 'red' : 'rgba(54, 162, 235, 0.2)'
  // const borderColor = isLow ? 'white' : 'rgb(54, 162, 235)';
  // const stepSize = Math.ceil(maxValue / 5 / 1000) * 1000;


  const { t } = useTranslation();
  const data = {
    labels: [t("dashboardPage.creditHeld")],
    datasets: [
      {
        label: '',
        type: "bar",
        data: [crdData[0]],
        borderColor: 'transparent',
        backgroundColor: percentage < 10 ? "rgba(0,0,0,0)" : barColor,
        borderWidth: 0,
        tension: 0.1,
        barPercentage: 0.3
      },
    ]
  };

  const options = {
    maintainAspectRatio: false,
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        boxWidth: 15,
        callbacks: {
          labelColor: (context) => ({
            borderColor: 'transparent',
            backgroundColor: barColor,
          }),
        },
      },
      title: { display: false },
      blinkingBar: {
        shouldBlink: percentage < 10,
        barColor: barColor,
      }
    },
    scales: {
      x: {
        min: 0,
        max: maxValue,
        ticks: {
          stepSize: stepSize,
        }
      },
    },
    elements: {
      bar: {
        borderWidth: 0,
        borderRadius: 6,
      }
    },
  };

  return (
    <ChartBox>
      <Bar ref={chartRef} options={options} data={data} width={"800px"} height={"320px"} />
    </ChartBox>
  )

}